import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { baseApiURL } from "../baseUrl";
import { 
  FiSearch, FiSend, FiUser, FiPhone, FiMail, FiBookOpen, 
  FiCalendar, FiMessageSquare, FiCopy, FiCheck, FiClock, 
  FiCheckCircle, FiAlertCircle, FiRefreshCw, FiExternalLink, FiShare2
} from "react-icons/fi";

const MessageParent = ({ userType = "Faculty", currentUser, initialEnrollmentNo = "" }) => {
  const [activeTab, setActiveTab] = useState("compose");
  const [searchRoll, setSearchRoll] = useState(initialEnrollmentNo);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [parentPortalLink, setParentPortalLink] = useState("");
  const [studentMessages, setStudentMessages] = useState([]);
  const [copiedLink, setCopiedLink] = useState(false);

  // Form State
  const [recipientType, setRecipientType] = useState("Primary");
  const [category, setCategory] = useState("General");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [selectedChannels, setSelectedChannels] = useState(["PORTAL", "SMS", "WHATSAPP", "EMAIL"]);
  const [sending, setSending] = useState(false);

  // History tab state
  const [historyMessages, setHistoryMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFilterCategory, setHistoryFilterCategory] = useState("All");

  useEffect(() => {
    if (initialEnrollmentNo) {
      setSearchRoll(initialEnrollmentNo);
      fetchStudentByRoll(initialEnrollmentNo);
    }
  }, [initialEnrollmentNo]);

  // Lookup student details by enrollment roll number
  const fetchStudentByRoll = async (roll) => {
    const queryRoll = roll || searchRoll;
    if (!queryRoll || !queryRoll.trim()) {
      toast.error("Please enter a valid student enrollment roll number.");
      return;
    }

    setLoadingStudent(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${baseApiURL()}/parent-message/lookup/${queryRoll.trim()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        setStudentData(res.data.student);
        setParentPortalLink(res.data.parentPortalLink);
        setStudentMessages(res.data.recentMessages || []);
        toast.success(`Found student: ${res.data.student.fullName}`);
      } else {
        setStudentData(null);
        toast.error(res.data.message || "Student not found");
      }
    } catch (err) {
      console.error(err);
      setStudentData(null);
      toast.error(err.response?.data?.message || "No student record found with this enrollment roll.");
    } finally {
      setLoadingStudent(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchStudentByRoll(searchRoll);
  };

  // Quick Message Templates
  const applyTemplate = (tplType) => {
    if (!studentData) {
      toast.error("Please search and select a student first!");
      return;
    }

    const sName = studentData.firstName || studentData.fullName;
    const sRoll = studentData.enrollmentNo;

    switch (tplType) {
      case "ATTENDANCE":
        setCategory("Attendance");
        setSubject(`Attendance Alert for ${sName} (${sRoll})`);
        setMessage(
          `Dear Parent,\nThis is to inform you that your ward ${sName} (${sRoll}) has accumulated low attendance in recent classes. Kindly ensure regular attendance to avoid academic detention.\nRegards,\n${userType} Office, Sphoorthy Engineering College.`
        );
        break;
      case "ACADEMIC":
        setCategory("Academic");
        setSubject(`Academic Progress Update - ${sName}`);
        setMessage(
          `Dear Parent,\nWe request you to review the recent internal examination performance for ${sName} (${sRoll}). Please visit the Parent Portal or meet the faculty during office hours to discuss progress.\nRegards,\nFaculty Incharge.`
        );
        break;
      case "DISCIPLINE":
        setCategory("Discipline");
        setSubject(`Important Discipline Notice - ${sName}`);
        setMessage(
          `Dear Parent,\nThis notice is issued regarding a conduct matter involving your ward ${sName} (${sRoll}) on campus. You are requested to contact the HOD / Mentor office at the earliest.\nRegards,\nCampus Administrative Office.`
        );
        break;
      case "FEE":
        setCategory("Fee");
        setSubject(`Fee Due Reminder for ${sName}`);
        setMessage(
          `Dear Parent,\nThis is a gentle reminder regarding pending academic/hostel/transport fee dues for your ward ${sName} (${sRoll}). Kindly clear the dues at the accounts section to prevent any disruption.\nThank you.`
        );
        break;
      case "MEETING":
        setCategory("General");
        setSubject(`Parent-Teacher Meeting Invitation - ${sName}`);
        setMessage(
          `Dear Parent,\nYou are cordially invited to attend the upcoming Parent-Teacher Meeting to discuss the academic growth and career development of ${sName} (${sRoll}).\nDate & Time: Saturday 10:00 AM.\nVenue: Department Seminar Hall.`
        );
        break;
      case "PRAISE":
        setCategory("General");
        setSubject(`Academic Appreciation for ${sName}!`);
        setMessage(
          `Dear Parent,\nWe are pleased to share that your ward ${sName} (${sRoll}) has demonstrated outstanding performance and commendable dedication in recent academic activities! Keep up the great work.\nRegards,\nDepartment Faculty.`
        );
        break;
      default:
        break;
    }
    toast.success("Template applied successfully!");
  };

  // Toggle notification channel chip
  const toggleChannel = (channel) => {
    if (selectedChannels.includes(channel)) {
      if (selectedChannels.length === 1) {
        toast.error("At least one notification channel must be selected.");
        return;
      }
      setSelectedChannels(selectedChannels.filter(c => c !== channel));
    } else {
      setSelectedChannels([...selectedChannels, channel]);
    }
  };

  // Send message submit
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!studentData) {
      toast.error("Please search and select a valid student first.");
      return;
    }

    if (!subject.trim()) {
      toast.error("Please enter a message subject.");
      return;
    }

    if (!message.trim()) {
      toast.error("Please enter the message body.");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        enrollmentNo: studentData.enrollmentNo,
        recipientType,
        subject: subject.trim(),
        message: message.trim(),
        category,
        channels: selectedChannels,
        senderId: currentUser?.loginid || currentUser?._id || "admin",
        senderName: currentUser?.name || currentUser?.loginid || `${userType} Staff`,
        senderRole: userType
      };

      const res = await axios.post(`${baseApiURL()}/parent-message/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        toast.success(res.data.message || "Message dispatched successfully!");
        setSubject("");
        setMessage("");
        // Add new message to local student messages
        if (res.data.data) {
          setStudentMessages([res.data.data, ...studentMessages]);
        }
      } else {
        toast.error(res.data.message || "Failed to send message.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Error sending message to parent.");
    } finally {
      setSending(false);
    }
  };

  // Copy parent portal link
  const copyLinkToClipboard = () => {
    if (!parentPortalLink) return;
    navigator.clipboard.writeText(parentPortalLink);
    setCopiedLink(true);
    toast.success("Parent Portal link copied to clipboard!");
    setTimeout(() => setCopiedLink(false), 3000);
  };

  // Fetch all history messages for tab 2
  const fetchAllHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem("token");
      let url = `${baseApiURL()}/parent-message/history?limit=100`;
      if (historyFilterCategory !== "All") {
        url += `&category=${historyFilterCategory}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setHistoryMessages(res.data.messages || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load global messaging history.");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      fetchAllHistory();
    }
  }, [activeTab, historyFilterCategory]);

  return (
    <div className="space-y-6">
      <div className="bento-header-banner flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 rounded-full text-[10px] font-bold text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800 mb-1">
            <FiMessageSquare /> Parent Communications Hub
          </div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            Message Parent by Enrollment Roll
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5 max-w-2xl">
            Lookup any student by roll number to communicate directly with parents via Parent Portal, SMS, WhatsApp, or Email.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center bg-slate-100 dark:bg-slate-950 p-1.5 rounded-xl border border-slate-200 dark:border-slate-800 self-start sm:self-auto">
          <button
            onClick={() => setActiveTab("compose")}
            className={`px-3.5 py-2 rounded-lg font-bold text-xs transition cursor-pointer flex items-center gap-1.5 ${
              activeTab === "compose"
                ? "bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/50 dark:border-slate-800"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <FiSend /> Compose Message
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-3.5 py-2 rounded-lg font-bold text-xs transition cursor-pointer flex items-center gap-1.5 ${
              activeTab === "history"
                ? "bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/50 dark:border-slate-800"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <FiClock /> Communication Logs
          </button>
        </div>
      </div>

      {activeTab === "compose" && (
        <div className="space-y-6">
          
          {/* Enrollment Roll Search Bar */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-xs border border-slate-200 dark:border-slate-800">
            <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row items-center gap-3">
              <div className="flex-1 w-full relative">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-base" />
                <input
                  type="text"
                  value={searchRoll}
                  onChange={(e) => setSearchRoll(e.target.value.toUpperCase())}
                  placeholder="Enter Student Enrollment Roll Number (e.g. 21TK1A0501)..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 text-xs font-bold tracking-wider outline-none text-slate-900 dark:text-white font-mono"
                />
              </div>
              <button
                type="submit"
                disabled={loadingStudent}
                className="w-full sm:w-auto px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-xs shadow-xs transition cursor-pointer flex items-center justify-center gap-2 shrink-0"
              >
                {loadingStudent ? (
                  <>
                    <FiRefreshCw className="animate-spin" /> Searching...
                  </>
                ) : (
                  <>
                    <FiSearch /> Search Roll
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Main Split Grid: Student Summary & Message Form */}
          {studentData ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left Card: Student & Parent Profile */}
              <div className="lg:col-span-1 space-y-6">
                
                {/* Student Details Card */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs space-y-5">
                  <div className="flex items-center gap-3.5 border-b border-slate-100 dark:border-slate-800 pb-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-base shadow-xs shrink-0">
                      {studentData.firstName ? studentData.firstName.charAt(0) : "S"}
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-widest bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-100 dark:border-emerald-800">
                        Active Student
                      </span>
                      <h2 className="text-sm font-bold text-slate-900 dark:text-white mt-1 leading-tight">
                        {studentData.fullName}
                      </h2>
                      <p className="text-xs font-mono font-semibold text-slate-400 dark:text-slate-500">{studentData.enrollmentNo}</p>
                    </div>
                  </div>

                  {/* Academic Metadata */}
                  <div className="grid grid-cols-2 gap-2.5 text-xs">
                    <div className="bg-slate-50 dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Branch</span>
                      <span className="font-bold text-slate-800 dark:text-slate-200">{studentData.branch}</span>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Semester</span>
                      <span className="font-bold text-slate-800 dark:text-slate-200">Sem {studentData.semester}</span>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Section</span>
                      <span className="font-bold text-slate-800 dark:text-slate-200">Section {studentData.section}</span>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Regulation</span>
                      <span className="font-bold text-slate-800 dark:text-slate-200">{studentData.regulation || "R20"}</span>
                    </div>
                  </div>

                  {/* Parents & Contact Details */}
                  <div className="space-y-2.5 pt-1">
                    <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Parent Contact Details</h3>
                    
                    <div className="p-3 bg-indigo-50/50 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-800/60 space-y-1">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-bold text-indigo-900 dark:text-indigo-300">Father:</span>
                        <span className="font-semibold text-slate-700 dark:text-slate-300">{studentData.FatherName}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-indigo-700 dark:text-indigo-400 font-mono font-bold">
                        <FiPhone size={12} /> {studentData.FatherPhoneNumber}
                      </div>
                    </div>

                    <div className="p-3 bg-rose-50/50 dark:bg-rose-950/40 rounded-xl border border-rose-100 dark:border-rose-800/60 space-y-1">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-bold text-rose-900 dark:text-rose-300">Mother:</span>
                        <span className="font-semibold text-slate-700 dark:text-slate-300">{studentData.MotherName}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-rose-700 dark:text-rose-400 font-mono font-bold">
                        <FiPhone size={12} /> {studentData.MotherPhoneNumber}
                      </div>
                    </div>

                    {studentData.email && (
                      <div className="p-2.5 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                        <span className="text-slate-400 font-bold">Email:</span>
                        <span className="font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[180px]">{studentData.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Parent Portal Link Box */}
                  {parentPortalLink && (
                    <div className="bg-slate-900 dark:bg-slate-950 text-white rounded-xl p-3.5 space-y-2 border border-slate-800">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-indigo-400 flex items-center gap-1.5">
                          <FiShare2 /> Dedicated Parent Portal
                        </span>
                        <button
                          onClick={copyLinkToClipboard}
                          className="text-xs text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 px-2 py-0.5 rounded border border-slate-700 transition flex items-center gap-1 cursor-pointer"
                        >
                          {copiedLink ? <FiCheck className="text-emerald-400" /> : <FiCopy />}
                          {copiedLink ? "Copied" : "Copy Link"}
                        </button>
                      </div>
                      <div className="text-[10px] font-mono text-slate-400 break-all bg-slate-950/80 p-2 rounded border border-slate-800/80">
                        {parentPortalLink}
                      </div>
                      <a
                        href={parentPortalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-indigo-400 hover:underline font-semibold"
                      >
                        Open Parent View <FiExternalLink size={12} />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Card: Message Composer Form */}
              <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 md:p-6 shadow-xs space-y-5">
                
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-3.5">
                  <div>
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-1.5 uppercase tracking-wider">
                      <FiSend className="text-indigo-600 dark:text-indigo-400" /> Compose Message to Parent
                    </h2>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Fill in message details or choose a quick template below.</p>
                  </div>

                  {/* Target Recipient Pills */}
                  <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-950 p-1 rounded-xl border border-slate-200 dark:border-slate-800">
                    {["Primary", "Father", "Mother", "Both"].map((rType) => (
                      <button
                        key={rType}
                        type="button"
                        onClick={() => setRecipientType(rType)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                          recipientType === rType
                            ? "bg-indigo-600 text-white shadow-xs"
                            : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                        }`}
                      >
                        {rType}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Template Chips */}
                <div>
                  <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-2">
                    Quick Message Templates
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => applyTemplate("ATTENDANCE")}
                      className="px-2.5 py-1 bg-amber-50 dark:bg-amber-950/60 hover:bg-amber-100 dark:hover:bg-amber-900/60 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs font-semibold rounded-xl transition cursor-pointer"
                    >
                      Low Attendance
                    </button>
                    <button
                      type="button"
                      onClick={() => applyTemplate("ACADEMIC")}
                      className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-200 dark:border-indigo-800 text-indigo-800 dark:text-indigo-300 text-xs font-semibold rounded-xl transition cursor-pointer"
                    >
                      Academic Alert
                    </button>
                    <button
                      type="button"
                      onClick={() => applyTemplate("DISCIPLINE")}
                      className="px-2.5 py-1 bg-rose-50 dark:bg-rose-950/60 hover:bg-rose-100 dark:hover:bg-rose-900/60 border border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-300 text-xs font-semibold rounded-xl transition cursor-pointer"
                    >
                      Discipline Notice
                    </button>
                    <button
                      type="button"
                      onClick={() => applyTemplate("FEE")}
                      className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-xs font-semibold rounded-xl transition cursor-pointer"
                    >
                      Fee Due Reminder
                    </button>
                    <button
                      type="button"
                      onClick={() => applyTemplate("MEETING")}
                      className="px-2.5 py-1 bg-purple-50 dark:bg-purple-950/60 hover:bg-purple-100 dark:hover:bg-purple-900/60 border border-purple-200 dark:border-purple-800 text-purple-800 dark:text-purple-300 text-xs font-semibold rounded-xl transition cursor-pointer"
                    >
                      Parent Meeting
                    </button>
                    <button
                      type="button"
                      onClick={() => applyTemplate("PRAISE")}
                      className="px-2.5 py-1 bg-cyan-50 dark:bg-cyan-950/60 hover:bg-cyan-100 dark:hover:bg-cyan-900/60 border border-cyan-200 dark:border-cyan-800 text-cyan-800 dark:text-cyan-300 text-xs font-semibold rounded-xl transition cursor-pointer"
                    >
                      Appreciation Note
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSendMessage} className="space-y-4">
                  
                  {/* Category Selection */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Message Category / Tag
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 text-xs font-semibold text-slate-900 dark:text-white outline-none"
                    >
                      <option value="General">General Notice</option>
                      <option value="Attendance">Attendance Alert</option>
                      <option value="Academic">Academic Performance</option>
                      <option value="Discipline">Behavior / Discipline</option>
                      <option value="Fee">Fee Reminder</option>
                      <option value="Emergency">Urgent Emergency</option>
                    </select>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Subject Line
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="e.g. Urgent Notice Regarding Attendance Shortage..."
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 text-xs font-medium text-slate-900 dark:text-white outline-none"
                      required
                    />
                  </div>

                  {/* Message Body */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                        Message Content
                      </label>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">
                        {message.length} characters
                      </span>
                    </div>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows="4"
                      placeholder="Write message content here..."
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 text-xs text-slate-900 dark:text-white leading-relaxed outline-none"
                      required
                    />
                  </div>

                  {/* Notification Channel Selection */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      Delivery Channels
                    </label>
                    <div className="flex flex-wrap gap-2.5">
                      {[
                        { id: "PORTAL", label: "In-App Parent Portal" },
                        { id: "SMS", label: "SMS Gateway" },
                        { id: "WHATSAPP", label: "WhatsApp Business" },
                        { id: "EMAIL", label: "Email Alert" }
                      ].map((ch) => {
                        const isSelected = selectedChannels.includes(ch.id);
                        return (
                          <button
                            key={ch.id}
                            type="button"
                            onClick={() => toggleChannel(ch.id)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold border transition cursor-pointer ${
                              isSelected
                                ? "bg-indigo-50 dark:bg-indigo-950/60 border-indigo-300 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 shadow-xs"
                                : "bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                            }`}
                          >
                            <div className={`w-3.5 h-3.5 rounded flex items-center justify-center border ${
                              isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 dark:border-slate-700"
                            }`}>
                              {isSelected && <FiCheck size={10} />}
                            </div>
                            <span>{ch.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Send Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center justify-center gap-2 uppercase tracking-wider cursor-pointer disabled:opacity-50"
                    >
                      {sending ? (
                        <>
                          <FiRefreshCw className="animate-spin text-sm" /> Dispatching Message...
                        </>
                      ) : (
                        <>
                          <FiSend className="text-sm" /> Send Message to Parent
                        </>
                      )}
                    </button>
                  </div>

                </form>
              </div>

            </div>
          ) : (
            /* Placeholder state before searching */
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center shadow-xs">
              <div className="w-14 h-14 bg-indigo-50 dark:bg-indigo-950/60 rounded-2xl text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-2xl mx-auto mb-3 border border-indigo-100 dark:border-indigo-800">
                <FiSearch />
              </div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">Search for a Student by Enrollment Roll</h3>
              <p className="text-slate-400 dark:text-slate-500 text-xs mt-1 max-w-md mx-auto">
                Enter an enrollment roll number in the search bar above to fetch student details and start composing a message to their parent.
              </p>
            </div>
          )}

          {/* Student Recent Messages Table */}
          {studentData && studentMessages.length > 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs space-y-3">
              <h3 className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5 uppercase tracking-wider">
                <FiClock className="text-indigo-600 dark:text-indigo-400" /> Past Communication Log for {studentData.fullName}
              </h3>
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="py-3 px-4 text-left">Date & Time</th>
                      <th className="py-3 px-4 text-left">Category</th>
                      <th className="py-3 px-4 text-left">Recipient</th>
                      <th className="py-3 px-4 text-left">Subject</th>
                      <th className="py-3 px-4 text-left">Sent By</th>
                      <th className="py-3 px-4 text-left">Channels</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                    {studentMessages.map((msg) => (
                      <tr key={msg._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                        <td className="py-3 px-4 font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap text-[11px]">
                          {new Date(msg.createdAt).toLocaleString(undefined, {
                            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                            msg.category === "Attendance" ? "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-800" :
                            msg.category === "Academic" ? "bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800" :
                            msg.category === "Discipline" ? "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-800" :
                            msg.category === "Fee" ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800" :
                            "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                          }`}>
                            {msg.category}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-semibold text-slate-900 dark:text-white">
                          {msg.recipientName || msg.recipientType}
                        </td>
                        <td className="py-3 px-4 font-medium text-slate-700 dark:text-slate-300 max-w-xs truncate">
                          {msg.subject}
                        </td>
                        <td className="py-3 px-4 text-slate-500 dark:text-slate-400">
                          {msg.senderName} ({msg.senderRole})
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            {(msg.sentVia || ["PORTAL"]).map((ch, idx) => (
                              <span key={idx} className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border border-slate-200 dark:border-slate-700">
                                {ch}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Global History Tab */}
      {activeTab === "history" && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 md:p-6 shadow-xs space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-3.5">
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-1.5 uppercase tracking-wider">
                <FiClock className="text-indigo-600 dark:text-indigo-400" /> Communication Dispatch History Log
              </h2>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Review all messages sent to parents by faculty and administrators.</p>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Filter:</span>
              <select
                value={historyFilterCategory}
                onChange={(e) => setHistoryFilterCategory(e.target.value)}
                className="px-3 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none"
              >
                <option value="All">All Categories</option>
                <option value="General">General</option>
                <option value="Attendance">Attendance</option>
                <option value="Academic">Academic</option>
                <option value="Discipline">Discipline</option>
                <option value="Fee">Fee</option>
                <option value="Emergency">Emergency</option>
              </select>
              <button
                onClick={fetchAllHistory}
                className="p-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl text-slate-700 dark:text-slate-300 transition cursor-pointer"
                title="Refresh Log"
              >
                <FiRefreshCw size={14} className={loadingHistory ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {loadingHistory ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : historyMessages.length === 0 ? (
            <div className="text-center py-12 text-slate-400 dark:text-slate-500 text-xs font-medium">
              No communication records found for the selected filter.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4 text-left">Date & Time</th>
                    <th className="py-3 px-4 text-left">Roll No</th>
                    <th className="py-3 px-4 text-left">Student</th>
                    <th className="py-3 px-4 text-left">Recipient</th>
                    <th className="py-3 px-4 text-left">Category</th>
                    <th className="py-3 px-4 text-left">Subject</th>
                    <th className="py-3 px-4 text-left">Sender</th>
                    <th className="py-3 px-4 text-left">Channels</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                  {historyMessages.map((msg) => (
                    <tr key={msg._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                      <td className="py-3 px-4 font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap text-[11px]">
                        {new Date(msg.createdAt).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                        {msg.enrollmentNo}
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-900 dark:text-white">
                        {msg.studentName}
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">
                        {msg.recipientName || msg.recipientType}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                          msg.category === "Attendance" ? "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-800" :
                          msg.category === "Academic" ? "bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800" :
                          msg.category === "Discipline" ? "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-800" :
                          msg.category === "Fee" ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800" :
                          "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                        }`}>
                          {msg.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-700 dark:text-slate-300 max-w-xs truncate">
                        {msg.subject}
                      </td>
                      <td className="py-3 px-4 text-slate-500 dark:text-slate-400">
                        {msg.senderName} ({msg.senderRole})
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          {(msg.sentVia || ["PORTAL"]).map((ch, idx) => (
                            <span key={idx} className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border border-slate-200 dark:border-slate-700">
                              {ch}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default MessageParent;
